import { deepSearchAndParseDates, ErrorMessage } from "@resume-studio/utils";
import _axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";

import { ADMIN_USER_KEY } from "../constants/query-keys";
import { toast } from "../hooks/use-toast";
import { refreshToken } from "../services/auth/refresh";
import { useAuthStore } from "../stores/auth";
import { queryClient } from "./query-client";

const apiBaseUrl = import.meta.env.VITE_PUBLIC_API_URL?.replace(/\/$/, "") ?? "/api";

// Dedicated admin axios instance. `withCredentials: true` is required so the
// browser ships the `admin_access_token` / `admin_refresh_token` cookies on
// every cross-origin call to the API. In dev this defaults to `/api` and is
// proxied by Vite; in production the standalone admin image can point directly
// at `https://api.resumestudio.io/api` via `VITE_PUBLIC_API_URL`.
export const axios = _axios.create({ baseURL: apiBaseUrl, withCredentials: true });

axios.interceptors.response.use(
  (response) => {
    const transformedResponse = deepSearchAndParseDates(response.data, ["createdAt", "updatedAt"]);
    return { ...response, data: transformedResponse };
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url ?? "";

    const message = error.response?.data?.message as ErrorMessage | undefined;
    const description =
      error.response?.data?.message === ErrorMessage.InvalidCredentials
        ? "Invalid credentials. Double-check your email and password."
        : (error.response?.data?.message as string | undefined);

    // Suppress the noisy "Unauthorized" toast for the auth bootstrap calls
    // — the AuthGuard will route the user to /login on its own. Same pattern
    // as the consumer app, just scoped to admin endpoints.
    const isAuthBootstrap = url.includes("/admin/auth/refresh") || url.includes("/admin/auth/me");
    const shouldToast = description && !(status === 401 && isAuthBootstrap);

    if (shouldToast) {
      toast({
        variant: "error",
        title: "Server error",
        description,
      });
    }

    if (message) {
      error.message = message;
    }

    return Promise.reject(error);
  },
);

// Separate instance used inside the refresh interceptor to break the
// retry-on-401 deadlock (without this, a 401 from /refresh itself would
// trigger another /refresh, ad infinitum). Same trick the consumer app uses.
const axiosForRefresh = _axios.create({ baseURL: apiBaseUrl, withCredentials: true });

const handleAuthError = () => refreshToken(axiosForRefresh);

const handleRefreshError = async (error: unknown) => {
  // Refresh failed → admin session is dead. Wipe local cache so the
  // AuthGuard will see "no user" and route to /login on the next render.
  // We do NOT call /logout here (the cookie is already invalid).
  queryClient.setQueryData(ADMIN_USER_KEY, null);
  useAuthStore.getState().setUser(null);
  return Promise.reject(error);
};

createAuthRefreshInterceptor(axios, handleAuthError, {
  statusCodes: [401, 403],
  shouldRefresh: () => Boolean(useAuthStore.getState().user),
});
createAuthRefreshInterceptor(axiosForRefresh, handleRefreshError);
